import React from 'react';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    TextField,
    Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

/**
 * Опции моделей, привязанные к провайдеру
 */
export const MODEL_OPTIONS = {
    OPEN_AI: [
        'gpt-5.1',
        'gpt-5',
        'gpt-5-mini',
        'gpt-5-nano',
        'gpt-5-pro',

        'gpt-4.1',
        'gpt-4.1-mini',
        'gpt-4.1-nano',
        'gpt-4.1-pro',

        'gpt-4o-mini'
    ],

    ANTHROPIC: [
        "claude-opus-4-5-20251101",
        "claude-haiku-4-5-20251001",
        "claude-sonnet-4-5-20250929",
        "claude-opus-4-1-20250805",
        "claude-opus-4-20250514",
        "claude-sonnet-4-20250514",
        "claude-3-7-sonnet-20250219",
        "claude-3-5-haiku-20241022",
        "claude-3-haiku-20240307",
    ],
    GROQ: [
        "llama-3.1-405b-reasoning",
        "llama-3.1-70b-versatile",
        "llama-3.1-8b-instant",

        "gemma2-27b-it",
        "gemma2-9b-it",

        "mixtral-8x7b-32768",

        "deepseek-r1-distill-llama-70b",
        "deepseek-r1-distill-qwen-32b",
    ],

    GROK: [],
    PERPLEXITY: [],

    GOOGLE_GEN_AI: [
        "gemini-flash-latest",
        "gemini-flash-lite-latest",

        "gemini-2.5-flash",
        "gemini-2.5-flash-lite",

        "gemini-2.0-flash",
        "gemini-2.0-flash-lite",

        "gemini-pro-latest"
    ]
};

// Все провайдеры = ключи словаря
export const MODEL_PROVIDERS = Object.keys(MODEL_OPTIONS);

/**
 * Универсальная форма выбора моделей и провайдеров.
 *
 * props:
 *   models: [{ modelName, modelProvider }]
 *   onModelsChange(nextModels)
 */
export function AiTestContainerModelsForm({ models, onModelsChange }) {

    const updateModel = (index, field, value) => {
        const updated = [...models];
        updated[index] = {
            ...updated[index],
            [field]: value
        };

        // Если сменили provider → сбрасываем имя модели, потому что список другой
        if (field === 'modelProvider') {
            updated[index].modelName = '';
        }

        onModelsChange(updated);
    };

    const addModel = () => {
        onModelsChange([
            ...models,
            {
                modelName: '',
                modelProvider: 'OPEN_AI'
            }
        ]);
    };

    const removeModel = (index) => {
        if (models.length === 1) return;
        const next = models.filter((_, i) => i !== index);
        onModelsChange(next);
    };

    return (
        <Box>
            {/* Заголовок + кнопка */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Модели для теста
                </Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={addModel}>
                    Добавить модель
                </Button>
            </Stack>

            {/* Список полей моделей */}
            <Stack spacing={2}>
                {models.map((m, index) => {
                    const options = MODEL_OPTIONS[m.modelProvider] || [];

                    return (
                        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2">
                                    Модель #{index + 1}
                                </Typography>

                                {models.length > 1 && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeModel(index)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>

                            <Stack mt={2} spacing={2}>
                                {/* modelName с автоподсказками + freeSolo */}
                                <Autocomplete
                                    freeSolo
                                    options={options}
                                    inputValue={m.modelName || ''}
                                    onInputChange={(event, newValue) =>
                                        updateModel(index, 'modelName', newValue)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="modelName"
                                            placeholder="выберите модель или введите свою"
                                            fullWidth
                                        />
                                    )}
                                />

                                {/* modelProvider */}
                                <FormControl fullWidth>
                                    <InputLabel id={`provider-${index}`}>
                                        modelProvider
                                    </InputLabel>
                                    <Select
                                        labelId={`provider-${index}`}
                                        label="modelProvider"
                                        value={m.modelProvider}
                                        onChange={(e) =>
                                            updateModel(index, 'modelProvider', e.target.value)
                                        }
                                    >
                                        {MODEL_PROVIDERS.map((prov) => (
                                            <MenuItem key={prov} value={prov}>
                                                {prov}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
}
